import { Button, Dialog, DialogActions, FormControlLabel, Switch, Typography } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { useCreateMailpiecesListCommentMutation } from '@api/piece';
import { Form, RHFTextField } from '@components/HookForm';
import { yupResolver } from '@hookform/resolvers/yup';

type Props = {
  open: boolean;
  handleClose: VoidFunction;
  pieceIds: number[];
  hasAnyUnsentPiece: boolean;
};

type FormValuesProps = {
  comment: string;
};

const DialogCommentPieces: FC<Props> = ({ open, handleClose, pieceIds, hasAnyUnsentPiece }) => {
  const [commentPieces, { isLoading }] = useCreateMailpiecesListCommentMutation();
  const [notifyMailroom, setNotifyMailroom] = useState(true);

  const CommentSchema = Yup.object().shape({
    comment: Yup.string().required('Comment is required'),
  });

  const defaultValues = {
    comment: '',
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(CommentSchema),
    defaultValues,
  });

  const { reset, handleSubmit } = methods;

  const onSubmit: SubmitHandler<FormValuesProps> = async ({ comment }) => {
    if (pieceIds.length) {
      try {
        await commentPieces({ pieceIds, text: comment, notifyMailroom }).unwrap();
        reset();
      } finally {
        handleClose();
      }
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle sx={{ pb: 2 }}>{`Add comment to ${pieceIds.length} items`}</DialogTitle>
      <Form methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 1 }}>
          {hasAnyUnsentPiece && (
            <Typography variant="body2" color="text.secondary" mb={3}>
              Some selected items haven't been sent yet, so those recipients won't be notified about the comment.
            </Typography>
          )}
          <RHFTextField name="comment" label="Comment *" multiline rows={3} />
          <FormControlLabel
            label="Notify mailroom users about comment"
            control={<Switch />}
            checked={notifyMailroom}
            onChange={() => setNotifyMailroom(!notifyMailroom)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={isLoading} variant="contained" color="primary">
            Post
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default DialogCommentPieces;
